import EventsCenter, { CustomEventsCenter } from '@Managers/Events/EventsCenter';
import { DATA_MANAGER_EVENTS } from './DataManagerEvents';

export type DataEachCallback = (parent: any, key: string | null, value: any, ...args: any[]) => void;

export class DataManager {
    parent: any;
    events: CustomEventsCenter;
    list: Record<string, any>;
    values: Record<string, any>;

    private _frozen: boolean;

    constructor(parent: any, eventEmitter?: typeof EventsCenter) {
        this.parent = parent;
        this.events = eventEmitter || (parent.events ? parent.events : parent);
        this.list = {};
        this.values = {};
        this._frozen = false;

        if (!parent.hasOwnProperty('sys') && this.events) {
            this.events.once(DATA_MANAGER_EVENTS.DESTROY, this.destroy, this);
        }
    }

    get(key: string | string[]): any {
        const list = this.list;

        if (Array.isArray(key)) {
            const output: any[] = [];

            for (const element of key) {
                output.push(list[element]);
            }

            return output;
        } else {
            return list[key];
        }
    }

    getAll(): Record<string, any> {
        const results: Record<string, any> = {};

        for (const key in this.list) {
            if (this.list.hasOwnProperty(key)) {
                results[key] = this.list[key];
            }
        }

        return results;
    }

    query(search: RegExp): Record<string, any> {
        const results: Record<string, any> = {};

        for (const key in this.list) {
            if (this.list.hasOwnProperty(key) && key.match(search)) {
                results[key] = this.list[key];
            }
        }

        return results;
    }

    set(key: string | Record<string, any>, data?: any): this {
        if (this._frozen) {
            return this;
        }

        if (typeof key === 'string') {
            return this.setValue(key, data);
        } else {
            for (const entry in key) {
                this.setValue(entry, key[entry]);
            }
        }

        return this;
    }

    inc(key: string, amount: number = 1): this {
        if (this._frozen) {
            return this;
        }

        let value = this.get(key);

        if (value === undefined) {
            value = 0;
        }

        this.set(key, value + amount);

        return this;
    }

    toggle(key: string): this {
        if (this._frozen) {
            return this;
        }

        this.set(key, !this.get(key));

        return this;
    }

    private setValue(key: string, data: any): this {
        if (this._frozen) {
            return this;
        }

        if (this.has(key)) {
            this.values[key] = data;
        } else {
            const list = this.list;
            const events = this.events;
            const parent = this.parent;

            Object.defineProperty(this.values, key, {
                enumerable: true,

                configurable: true,

                get: () => list[key],

                set: (value: any) => {
                    if (!this._frozen) {
                        const previousValue = list[key];
                        list[key] = value;

                        events.emit(DATA_MANAGER_EVENTS.CHANGE_DATA, { parent, key, value, previousValue });
                        events.emit(DATA_MANAGER_EVENTS.CHANGE_DATA_KEY + key, { parent, value, previousValue });
                    }
                }
            });

            list[key] = data;

            events.emit(DATA_MANAGER_EVENTS.SET_DATA, { parent, key, data });
        }

        return this;
    }

    each(callback: DataEachCallback, context?: any, ...args: any[]): this {
        const callbackArgs: any[] = [this.parent, null, undefined, ...args];

        for (const key in this.list) {
            callbackArgs[1] = key;
            callbackArgs[2] = this.list[key];

            (callback as Function).apply(context, callbackArgs);
        }

        return this;
    }

    merge(data: Record<string, any>, overwrite: boolean = true): this {
        for (const key in data) {
            if (data.hasOwnProperty(key) && (overwrite || (!overwrite && !this.has(key)))) {
                this.setValue(key, data[key]);
            }
        }

        return this;
    }

    remove(key: string | string[]): this {
        if (this._frozen) {
            return this;
        }

        if (Array.isArray(key)) {
            for (const element of key) {
                this.removeValue(element);
            }
        } else {
            return this.removeValue(key);
        }

        return this;
    }

    private removeValue(key: string): this {
        if (this.has(key)) {
            const data = this.list[key];

            delete this.list[key];
            delete this.values[key];

            this.events.emit(DATA_MANAGER_EVENTS.REMOVE_DATA, { parent: this.parent, key, data });
        }

        return this;
    }

    pop(key: string): any {
        let data: any = undefined;

        if (!this._frozen && this.has(key)) {
            data = this.list[key];

            delete this.list[key];
            delete this.values[key];

            this.events.emit(DATA_MANAGER_EVENTS.REMOVE_DATA, { parent: this.parent, key, data });
        }

        return data;
    }

    has(key: string): boolean {
        return this.list.hasOwnProperty(key);
    }

    setFreeze(value: boolean): this {
        this._frozen = value;

        return this;
    }

    reset(): this {
        for (const key in this.list) {
            delete this.list[key];
            delete this.values[key];
        }

        this._frozen = false;

        return this;
    }

    destroy(): void {
        this.reset();

        this.events.off(DATA_MANAGER_EVENTS.CHANGE_DATA);
        this.events.off(DATA_MANAGER_EVENTS.SET_DATA);
        this.events.off(DATA_MANAGER_EVENTS.REMOVE_DATA);

        this.parent = null;
    }

    get freeze(): boolean {
        return this._frozen;
    }

    set freeze(value: boolean) {
        this._frozen = !!value;
    }

    get count(): number {
        let i = 0;

        for (const key in this.list) {
            if (this.list[key] !== undefined) {
                i++;
            }
        }

        return i;
    }
}

export default DataManager;
